import React, { useEffect, useState } from 'react';
import {
  Text,
  Button,
  makeStyles,
  shorthands,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import {
  ArrowTrendingLines20Regular,
  ShieldError20Regular,
  Board20Regular,
  PeopleTeam20Regular,
  ArrowUp20Regular,
  MoneyHand20Regular,
  People20Regular,
  ArrowRight16Regular,
} from '@fluentui/react-icons';
import { dataModelsService, InsightResult } from '../../api/dataModelsService';
import { MutationButton, useWorkspaceAccess } from '../security/WorkspaceAccess';

// ─── Category Icon Colors (desaturated ~10%) ─────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  pipeline: '#2d9a2d',      // Green (softer)
  revenue: '#c9484b',       // Red (softer)
  engagement: '#3a8fd4',    // Blue (softer)
  conversion: '#9474c0',    // Purple (softer)
  concentration: '#d06828', // Orange (softer)
  quality: '#b8a020',       // Amber (softer)
  forecast: '#2a9194',      // Teal (softer)
  risk: '#c9484b',          // Red (softer)
};

const useStyles = makeStyles({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '32px',
  },
  header: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '10px',
  },
  title: {
    fontSize: '20px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  freshness: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '12px',
  },
  card: {
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
    ...shorthands.padding('16px', '18px'),
    ...shorthands.borderRadius('14px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground1,
    transitionProperty: 'box-shadow, transform',
    transitionDuration: '180ms',
    transitionTimingFunction: 'ease',
    ':hover': {
      boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
      transform: 'translateY(-1px)',
    },
  },
  cardIcon: {
    flexShrink: 0,
    width: '18px',
    display: 'flex',
    alignItems: 'flex-start',
    paddingTop: '1px',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.35',
    overflowY: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  badges: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '6px',
  },
  categoryBadge: {
    fontSize: '10px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground4,
    textTransform: 'capitalize',
  },
  cardDescription: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.45',
    overflowY: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    marginTop: '14px',
  },
  cardFooter: {
    marginTop: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  footerActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '4px',
    marginTop: 'auto',
  },
  viewLabel: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground4,
    fontWeight: 400,
  },
  exploreLink: {
    fontSize: '11px',
    fontWeight: 500,
    color: tokens.colorNeutralForeground3,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '3px',
    ':hover': {
      color: tokens.colorNeutralForeground1,
    },
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    ...shorthands.padding('32px', '0'),
    gap: '8px',
  },
});

function getInsightIcon(category: string | null, insightType: string) {
  const color = CATEGORY_COLORS[category || ''] || tokens.colorNeutralForeground3;
  const style = { color, fontSize: '16px', width: '16px', height: '16px' };

  const cat = category || insightType;
  switch (cat) {
    case 'pipeline':
    case 'trend':
      return <ArrowTrendingLines20Regular style={style} />;
    case 'quality':
    case 'risk':
      return <ShieldError20Regular style={style} />;
    case 'concentration':
      return <PeopleTeam20Regular style={style} />;
    case 'conversion':
      return <ArrowUp20Regular style={style} />;
    case 'revenue':
      return <MoneyHand20Regular style={style} />;
    case 'engagement':
      return <People20Regular style={style} />;
    default:
      return <Board20Regular style={style} />;
  }
}

const SEVERITY_STYLES: Record<string, { label: string; bg: string; color: string }> = {
  critical: { label: 'Critical', bg: tokens.colorPaletteRedBackground2, color: tokens.colorPaletteRedForeground1 },
  high: { label: 'High', bg: tokens.colorPaletteDarkOrangeBackground2, color: tokens.colorPaletteDarkOrangeForeground1 },
  medium: { label: 'Medium', bg: tokens.colorPaletteYellowBackground2, color: tokens.colorPaletteYellowForeground2 },
  info: { label: 'Info', bg: tokens.colorNeutralBackground4, color: tokens.colorNeutralForeground3 },
  low: { label: 'Info', bg: tokens.colorNeutralBackground4, color: tokens.colorNeutralForeground3 },
};

function getSeverityBadge(severity: string) {
  const s = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.info!;
  return (
    <span style={{
      fontSize: '11px',
      fontWeight: 500,
      padding: '0px 10px',
      borderRadius: '6px',
      backgroundColor: s.bg,
      color: s.color,
      whiteSpace: 'nowrap',
    }}>
      {s.label}
    </span>
  );
}

function formatCategoryLabel(category: string | null): string {
  if (!category) return 'Insight';
  return category.charAt(0).toUpperCase() + category.slice(1);
}

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

interface KeyInsightsSectionProps {
  modelId: string;
  onCreateDashboard?: (viewId: string) => void;
  onExplore?: (viewId: string) => void;
}

const KeyInsightsSection: React.FC<KeyInsightsSectionProps> = ({
  modelId,
  onCreateDashboard,
  onExplore,
}) => {
  const styles = useStyles();
  const { runMutation } = useWorkspaceAccess();
  const [insights, setInsights] = useState<InsightResult[]>([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    dataModelsService.getInsights(modelId).then((res) => {
      if (!cancelled) {
        setInsights(res.insights);
        setLastRefreshedAt(res.lastRefreshedAt);
        setLoading(false);
      }
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [modelId]);

  if (loading) {
    return (
      <div className={styles.section}>
        <Text className={styles.title}>Key Insights</Text>
        <Spinner size="small" />
      </div>
    );
  }

  if (insights.length === 0) {
    return (
      <div className={styles.section}>
        <Text className={styles.title}>Key Insights</Text>
        <div className={styles.emptyState}>
          <Text style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }}>
            No key insights yet
          </Text>
          <Text style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
            We couldn't find enough data patterns to generate insights for this model.
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <Text className={styles.title}>Key Insights</Text>
        {lastRefreshedAt && (
          <Text className={styles.freshness}>
            Updated {formatTimeAgo(lastRefreshedAt)}
          </Text>
        )}
      </div>
      <div className={styles.grid}>
        {insights.map((insight) => (
          <div key={insight.id} className={styles.card}>
            <span className={styles.cardIcon}>
              {getInsightIcon(insight.category, insight.insightType)}
            </span>
            <div className={styles.cardContent}>
              <Text className={styles.cardTitle}>{insight.title}</Text>
              <div className={styles.badges}>
                {getSeverityBadge(insight.severity)}
                <span className={styles.categoryBadge}>
                  {formatCategoryLabel(insight.category)}
                </span>
              </div>
              <Text className={styles.cardDescription}>{insight.description}</Text>
              {insight.sourceViewId && (
                <div className={styles.cardFooter}>
                  {insight.sourceViewName && (
                    <Text className={styles.viewLabel}>
                      Generated from: {insight.sourceViewName}
                    </Text>
                  )}
                  <div className={styles.footerActions}>
                    <MutationButton
                      size="small"
                      appearance="primary"
                      icon={<Board20Regular />}
                      onClick={() => runMutation(() => onCreateDashboard?.(insight.sourceViewId!))}
                    >
                      Create Dashboard
                    </MutationButton>
                    <Button
                      appearance="transparent"
                      size="small"
                      icon={<ArrowRight16Regular />}
                      iconPosition="after"
                      onClick={() => onExplore?.(insight.sourceViewId!)}
                      className={styles.exploreLink}
                    >
                      Explore
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KeyInsightsSection;